import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Bookings from "@/pages/Bookings";
import BookingsTrash from "@/pages/BookingsTrash";
import PostProduction from "@/pages/PostProduction";
import CalendarView from "@/pages/CalendarView";
import Users from "@/pages/Users";
import GalleryList from "@/pages/GalleryList";
import GalleryDetail from "@/pages/GalleryDetail";
import GalleryDebugSearch from "@/pages/GalleryDebugSearch";
import PublicGallery from "@/pages/PublicGallery";
import RequestOriginals from "@/pages/RequestOriginals";
import Team from "@/pages/Team";
import OfficeExpenses from "@/pages/OfficeExpenses";
import Financials from "@/pages/Financials";
import Settings from "@/pages/Settings";
import MyEarnings from "@/pages/MyEarnings";
import BackupRecovery from "@/pages/BackupRecovery";
import PaymentProofs from "@/pages/PaymentProofs";
import Leads from "@/pages/Leads";
import ResetPassword from "@/pages/ResetPassword";
import ContractSign from "@/pages/ContractSign";
import ClientTracker from "@/pages/ClientTracker";
import Inquiry from "@/pages/Inquiry";
import PublicProposal from "@/pages/PublicProposal";
import SplitTracker from "@/pages/SplitTracker";
// iter123 — Mount the sonner Toaster at the app root so public routes
// (Client Tracker, Contract Sign) also render toast feedback.
import { Toaster } from "sonner";

function Protected({ children, adminOnly = false, blockViewer = false, financeBlind = false, blockManager = false, module = null, minLevel = "view_only" }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  // iter184 — When a `module` is declared, the effective permission map
  // is the authoritative gate. Owners + admin always pass. This means
  // an Admin who grants a coordinator `bookings=full_control` no longer
  // gets the coord silently bounced by the coarse `financeBlind` flag —
  // saved user permissions really are the single source of truth.
  const rank = { no_access: 0, view_only: 1, edit: 2, full_control: 3 };
  const effectiveMap = user.permissions_effective || {};
  const modulePerm = module ? (effectiveMap[module] || "no_access") : null;
  const hasModulePerm = module && ((rank[modulePerm] || 0) >= (rank[minLevel] || 1));

  // Admin-only pages bounce coordinators/staff to their Team workspace (which
  // strips out payroll & roster tabs at the UI + API layer).
  if (adminOnly && user.role !== "admin" && !user.is_owner) return <Navigate to="/team" replace />;
  if (blockViewer && user.role === "viewer" && !hasModulePerm) return <Navigate to="/bookings" replace />;
  // Financial Blindness — coordinator + staff never see the money-side of the app
  // UNLESS Admin explicitly grants the module permission to that user.
  if (financeBlind && user.role !== "admin" && !user.is_owner && !hasModulePerm) return <Navigate to="/team" replace />;
  // iter137 — Manager is fully removed from Bookings / Inquiries surfaces
  // unless Admin explicitly grants module access to that user.
  if (blockManager && user.role === "manager" && !hasModulePerm) return <Navigate to="/" replace />;

  // iter157/184 — Central permission gate. When the caller declares a
  // `module`, hand-typed URLs also get blocked when the effective
  // level is below `minLevel`. Owners short-circuit.
  if (module && !user.is_owner && !hasModulePerm) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "viewer") return <Navigate to="/bookings" replace />;
  // iter184 — For coord/staff we still prefer /team as the landing when
  // they actually retain team access, but if an Admin has explicitly set
  // Team=No Access we fall through to the Dashboard (which will also
  // only render if the user has dashboard permission).
  const perms = user.permissions_effective || {};
  const rank = { no_access: 0, view_only: 1, edit: 2, full_control: 3 };
  const teamLevel = perms.team || "no_access";
  if (user.role === "coordinator" && (rank[teamLevel] || 0) >= 1) {
    return <Navigate to="/team" replace />;
  }
  return <Dashboard />;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (user) {
    if (user.role === "viewer") return <Navigate to="/bookings" replace />;
    if (user.role === "coordinator") return <Navigate to="/team" replace />;
    return <Navigate to="/" replace />;
  }
  return <Login />;
}

export default function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/inquiry" element={<Inquiry />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/g/:eventId/originals" element={<RequestOriginals />} />
          <Route path="/g/:eventId" element={<PublicGallery />} />
          <Route path="/contracts/:token" element={<ContractSign />} />
          <Route path="/track/:token" element={<ClientTracker />} />
          <Route path="/track/split/:token" element={<SplitTracker />} />
          <Route path="/proposal/:token" element={<PublicProposal />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<HomeRoute />} />
            <Route path="leads" element={<Protected financeBlind blockManager module="leads"><Leads /></Protected>} />
            <Route path="bookings" element={<Protected financeBlind blockManager module="bookings"><Bookings /></Protected>} />
            <Route path="bookings/trash" element={<Protected financeBlind blockManager module="bookings" minLevel="edit"><BookingsTrash /></Protected>} />
            <Route
              path="post-production"
              element={
                <Protected financeBlind module="post_production">
                  <PostProduction />
                </Protected>
              }
            />
            <Route
              path="calendar"
              element={
                <Protected financeBlind module="dashboard">
                  <CalendarView />
                </Protected>
              }
            />
            <Route
              path="gallery"
              element={
                <Protected financeBlind module="gallery">
                  <GalleryList />
                </Protected>
              }
            />
            <Route
              path="gallery/:eventId"
              element={
                <Protected financeBlind module="gallery">
                  <GalleryDetail />
                </Protected>
              }
            />
            <Route
              path="gallery/:eventId/debug-search"
              element={
                <Protected financeBlind module="gallery">
                  <GalleryDebugSearch />
                </Protected>
              }
            />
            <Route
              path="team"
              element={
                <Protected blockViewer module="team">
                  <Team />
                </Protected>
              }
            />
            <Route
              path="office-expenses"
              element={
                <Protected blockViewer module="cash_book">
                  <OfficeExpenses />
                </Protected>
              }
            />
            <Route
              path="financials"
              element={
                <Protected adminOnly>
                  <Financials />
                </Protected>
              }
            />
            <Route
              path="settings"
              element={
                <Protected adminOnly>
                  <Settings />
                </Protected>
              }
            />
            <Route
              path="users"
              element={
                <Protected adminOnly>
                  <Users />
                </Protected>
              }
            />
            {/* iter163 — Read-only "My Earnings" for any logged-in user.
                Backend endpoint gates by user_id → staff link. */}
            <Route
              path="my-earnings"
              element={
                <Protected>
                  <MyEarnings />
                </Protected>
              }
            />
            {/* iter170 — Admin-only Backup & Recovery */}
            <Route
              path="backup"
              element={
                <Protected adminOnly>
                  <BackupRecovery />
                </Protected>
              }
            />
            {/* iter181 — Admin-only Payment Proofs review */}
            <Route
              path="payment-proofs"
              element={
                <Protected adminOnly>
                  <PaymentProofs />
                </Protected>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
      </PermissionsProvider>
    </AuthProvider>
  );
}
